import { Box, Stack } from '@mui/material';
import { useAtom } from 'jotai';

import VerticalBarChart from '@/components/charts/VerticalBarChart';
import GenericChartWidget from '@/components/GenericChartWidget';
import LoadingIndicator from '@/components/LoadingIndicator';
import useStyles from '@/pages/Administration/Devices/LimaSourceTrendChart/LimaSourceTrendChart.styles';

import { LIMA_SOURCES_TREND_BAR_SERIES } from '../Devices.constants';
import useFetchLimaSourceEventsPerDay from './LimaSourceTrendChart.hooks';
import { limaSourceEventsByDayAtom } from './LimaSourceTrendChart.store';

const LimaSourceTrendChart = () => {
  const { classes } = useStyles();
  const { isFetching: isFetchingLimaSourceEvents } =
    useFetchLimaSourceEventsPerDay();
  const [limaSourceEventsByHour] = useAtom(limaSourceEventsByDayAtom);

  // Average events per hour
  const totalEvents = new Intl.NumberFormat('en-US').format(
    Math.round(limaSourceEventsByHour.total_events / 30 / 24)
  );

  // Displaying total size in a human-readable format
  const formatSize = (bytes: number): { value: string; unit: string } => {
    const sizeInKb = bytes / 1024;
    const sizeInMb = sizeInKb / 1024;
    const sizeInGb = sizeInMb / 1024;

    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    });

    if (sizeInGb >= 1) {
      return { value: formatter.format(sizeInGb), unit: 'GB' };
    }
    if (sizeInMb >= 1) {
      return { value: formatter.format(sizeInMb), unit: 'MB' };
    }

    return { value: formatter.format(sizeInKb), unit: 'KB' };
  };

  // Average Log Ingest Size per hour
  const averageBytes = limaSourceEventsByHour.total_size / 30 / 24;
  const totalSize = formatSize(averageBytes);

  return (
    <Box className={classes.chartWrapper}>
      {isFetchingLimaSourceEvents ? (
        <LoadingIndicator noBackground isLoading />
      ) : (
        limaSourceEventsByHour && (
          <GenericChartWidget
            title={
              <Stack direction="column">
                <span>Average Events Per Hour</span>
                <span>Average Ingest Size Per Hour {totalSize.unit}</span>
              </Stack>
            }
            headerRightContent={
              <Stack direction="column">
                <span>{totalEvents}</span>
                <span>{totalSize.value}</span>
              </Stack>
            }
          >
            <VerticalBarChart
              data={limaSourceEventsByHour.data}
              series={LIMA_SOURCES_TREND_BAR_SERIES}
              height={50}
              suppressExponentialAxis
              suppressYAxis
              suppressXAxis
              suppressCrosshairLine
            />
          </GenericChartWidget>
        )
      )}
    </Box>
  );
};

export default LimaSourceTrendChart;
